import { createEffect, createSignal, onCleanup, onMount, Show, useContext } from 'solid-js';
import { ErrorCatcher } from '../../tools/error-catcher';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { CookieModal } from '../../ui-components/cookie-modal/cookie-modal';
import { TabHandler } from '../../tools/tab-handler';
import { PlayIcon } from '../icons-library/play';

type PodcastProps = {
    podcastProvider: string;
    id: string;
    podcastId: string;
};

/**
 * The CVI play control, ported from the DS `ds-button-play` at its `md` size:
 * a 56px circle with a 24px glyph, filled with the primary button colour on a
 * separate layer at 90% opacity so the card reads through, going solid on hover.
 *
 * A `<span>` rather than a `<button>`: the whole consent card is the button and
 * nesting buttons is invalid HTML, so the hover state comes from the card via
 * `group-hover`. Decorative — the card's own label names the action.
 */
const PlayGlyph = () => (
    <span class="relative flex size-cvi-12 shrink-0 items-center justify-center overflow-hidden rounded-cvi-round text-cvi-fg-inverse">
        <span
            class={[
                'absolute inset-0 z-0 bg-[var(--component-button-color-primary-default-bg-color)] opacity-90',
                'transition-[background-color,opacity] duration-200 ease-cvi-standard',
                'group-hover:bg-[var(--component-button-color-primary-hover-bg-color)] group-hover:opacity-100',
                'group-active:bg-[var(--component-button-color-primary-press-bg-color)] group-active:opacity-100',
            ].join(' ')}
        />
        <PlayIcon size="default" ariaHidden={true} class="relative z-[1]" />
    </span>
);

export const Podcast = (props: PodcastProps) => {
    const [ elementId, setElementId ] = createSignal('');
    const [ cookiesAccepted, setCookiesAccepted ] = createSignal(false);
    const [ showModal, setShowModal ] = createSignal(false);

    // Accessibility/keyboard-controls related
    const [ firstTabbableElement, setFirstTabbableElement ] = createSignal<HTMLElement | undefined>();
    const [ lastTabbableElement, setLastTabbableElement ] = createSignal<HTMLElement | undefined>();
    
    const { localize } = useContext(AppContext);

    onMount(() => {
        checkConsent();

        // Listen for changes in consent
        window.addEventListener('CookiebotOnAccept', () => {
            checkConsent();
        });

        onCleanup(() => {
            window.removeEventListener('CookiebotOnAccept', checkConsent);
        });
    });

    createEffect(() => {
        if (!cookiesAccepted()) {
            return;
        }

        if (props.podcastProvider !== 'buzzsprout' || !props.podcastId || !props.id) {
            return;
        }

        if (!document) {
            return;
        }

        const elementId = Math.random().toString(36).substring(7);
        setElementId(elementId);

        const scriptTag = document.createElement('script');
        scriptTag.type = 'text/javascript';
        scriptTag.dataset.cookieconsent = 'marketing';
        scriptTag.src = `https://www.buzzsprout.com/${props.podcastId}/${props.id}.js?container_id=buzzsprout-${elementId}&player=small`;
        document.head.appendChild(scriptTag);
    });

    const checkConsent = () => {
        if (window?.Cookiebot?.consent?.marketing) {
            setCookiesAccepted(true);
        }
    };

    return (
        <ErrorCatcher componentName="Podcast">
            <div class="my-cvi-8 tablet:my-cvi-10">
                <Show when={!cookiesAccepted()}>
                    {/* Same flush treatment as the card media boxes: no border, no hover shadow,
                        the neutral-subtle fill on an 8px radius with ds-space(7)/ds-space(8)
                        padding. Hover deepens the fill one step, like secondary-neutral. */}
                    <button
                        type="button"
                        aria-haspopup="dialog"
                        class={[
                            'group flex w-full items-center justify-center gap-cvi-6 px-cvi-8 py-cvi-7',
                            'rounded-cvi-md border-0 bg-cvi-bg-neutral-subtle',
                            'cursor-pointer transition-colors duration-200 ease-cvi-standard',
                            'hover:bg-cvi-bg-block-neutral-strong',
                        ].join(' ')}
                        onClick={() => setShowModal(true)}
                    >
                        <PlayGlyph />
                        <span class="heading-lg text-left text-cvi-fg-default">
                            {localize('play-podcast-episode', 'Play podcast episode')}
                        </span>
                    </button>
                </Show>

                <Show when={cookiesAccepted()}>
                    <Show when={props.podcastProvider === 'buzzsprout' && props.id}>
                        <div id={`buzzsprout-${elementId()}`} />
                    </Show>

                    {/* 200px is the Captivate player's own fixed height — it doesn't reflow. */}
                    <Show when={props.podcastProvider === 'captivate' && props.id}>
                        <div class="h-[200px] w-full overflow-hidden rounded-cvi-md">
                            <iframe
                                class="size-full border-0"
                                title={localize('podcast-player', 'Podcast player')}
                                loading="lazy"
                                src={`https://player.captivate.fm/episode/${props.id}`}
                            />
                        </div>
                    </Show>
                </Show>
            </div>

            <Show when={showModal()}>
                <TabHandler refs={{
                    firstTabbableElement,
                    lastTabbableElement,
                }}>
                    <CookieModal
                        close={() => setShowModal(false)}
                        setFirstTabbableElement={(el: HTMLElement) => setFirstTabbableElement(el)}
                        setLastTabbableElement={(el: HTMLElement) => setLastTabbableElement(el)}
                    />
                </TabHandler>
            </Show>
        </ErrorCatcher>
    );
};

Podcast.parseProps = (atts: any) => {
    return {
        podcastProvider: atts.podcastProvider,
        id: atts.id,
        podcastId: atts.podcastId,
    };
};
